// Keeps open views in step with invoice changes made by other users, off the back of the
// PushEventService poll. An invoice shows up in two unrelated places, so this service has two
// subscriptions rather than one:
//
//   subscribeInvoiceList          - a patient's invoice list (the billing modal)
//   subscribeAppointmentInvoices  - the invoice icon and hover total on a calendar appointment
//
// plus watchInvoice, which patches nothing and only tells an open payment form its invoice has moved on.
//
// The two subscriptions patch a single row rather than re-fetching the view. An invoice event carries
// {invoiceId, patientId}, which is enough to decide relevance before spending a request: a list showing
// another patient, or a calendar with no appointment for that patient on screen, costs zero requests. A
// delete needs no request at all - the id alone says what to drop. Nor is a request worth making for a
// user who is not authorised to read invoices at all - see canFetchInvoices(). And every request goes
// through LiveUpdateGuard, so one that keeps failing anyway - a Xero grant revoked mid-session, say - is
// stopped after a few tries instead of failing on every invoice event for the rest of the session.
app.service("InvoiceLiveUpdateService", [
	"BillingAPI",
	"AppointmentAPI",
	"PUSH_EVENTS",
	"Session",
	"LiveUpdateGuard",
	"$q",
	"$timeout",
	function (BillingAPI, AppointmentAPI, PUSH_EVENTS, Session, LiveUpdateGuard, $q, $timeout) {
		/** The invoice event types that mean "this invoice now exists, or now looks different". */
		const UPSERT_EVENTS = [PUSH_EVENTS.INVOICE_ADDED, PUSH_EVENTS.INVOICE_UPDATED, PUSH_EVENTS.INVOICE_PAID];

		/** Whether two ids name the same thing. Ids reach us by different routes and differ in type. */
		function sameId(a, b) {
			return a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
		}

		/** The event's payload, under the names this service uses. */
		function toChange(pushEvent) {
			const data = (pushEvent && pushEvent.data) || {};
			return {
				invoiceId: data.invoiceId,
				patientId: data.patientId,
			};
		}

		/** The index of an invoice in a list of invoices or invoice summaries, or -1. */
		function indexOfInvoice(list, invoiceId) {
			for (let i = 0; i < list.length; i++) {
				if (sameId(list[i].invoice_id, invoiceId)) {
					return i;
				}
			}
			return -1;
		}

		/** Drops every copy of an invoice from a list. Returns whether anything was removed. */
		function removeInvoice(list, invoiceId) {
			let removed = false;
			for (let i = list.length - 1; i >= 0; i--) {
				if (sameId(list[i].invoice_id, invoiceId)) {
					list.splice(i, 1);
					removed = true;
				}
			}
			return removed;
		}

		// Whether this user may fetch invoices, and the practice that answer was reached for.
		let canFetch = null;
		let canFetchPracticeId = null;

		/** The probe in flight, so a burst of events in one poll batch shares a single request. */
		let canFetchProbe = null;

		/**
		 * Resolves whether the signed-in user may fetch invoices for the current practice.
		 *
		 * The invoice endpoints answer an unauthorised user with a 401 *and* an exception-audit row. These
		 * fetches run off a practice-wide push event, so without this gate every user with a calendar open
		 * logs one of those for every invoice anyone else in the practice touches - invisibly, since the
		 * view simply keeps the numbers it already has. GET /api/billing/auth puts the same question to the
		 * same backend check but answers 200 with a null logged_in_to, so reading it costs no row.
		 *
		 * It is asked on the first invoice event rather than at view load: it is not free on the happy path
		 * - for an authorised user the endpoint calls Xero/MYOB for accounts and tax rates - and most
		 * sessions never see an invoice event at all.
		 */
		function canFetchInvoices() {
			const practiceId = Session.practice && Session.practice.id;

			// Decided here rather than asked: /api/billing/auth answers a practice without the billing
			// feature with the very 401 and audit row this exists to avoid.
			if (!practiceId || !Session.practice.practiceFeatures || Session.practice.practiceFeatures.billingFeature !== true) {
				return $q.resolve(false);
			}

			if (!sameId(practiceId, canFetchPracticeId)) {
				canFetchPracticeId = practiceId;
				canFetch = null;
				canFetchProbe = null;
			}

			if (canFetch !== null) {
				return $q.resolve(canFetch);
			}

			// Already answered for this practice by whoever opened the billing modal or the profile menu.
			if (Session.xeroLogin === false) {
				canFetch = false;
				return $q.resolve(false);
			}

			if (!canFetchProbe) {
				const probe = LiveUpdateGuard.run("invoice", function () {
					return BillingAPI.getLoggedIn();
				}).then(
					function (resp) {
						// The practice changed while this was in flight: the answer is for the old one.
						if (canFetchProbe !== probe) {
							return false;
						}
						const settings = (resp && resp.data) || {};
						// An organisation conflict is authorised but unusable: the invoice endpoints reject a user
						// whose practice resolves to more than one organisation, with an audit row of its own.
						canFetch = !!settings.logged_in_to && settings.error_organisation_conflict !== true;
						canFetchProbe = null;

						// Shared, so opening the billing modal next does not ask again, and so the profile menu
						// can show Login/Logout Xero from a real answer rather than assuming logged out.
						Session.setXeroLoginState(!!settings.logged_in_to);
						return canFetch;
					},
					function () {
						// Left undecided: a probe that never landed is not an answer, and the next event should be
						// free to ask again. Nothing is logged - the response carries billing data.
						if (canFetchProbe === probe) {
							canFetchProbe = null;
						}
						return false;
					}
				);
				canFetchProbe = probe;
			}

			return canFetchProbe;
		}

		/**
		 * The invoice an event refers to, or null when it must not be asked for, could not be fetched, or
		 * came back without an id.
		 */
		function fetchInvoice(patientId, invoiceId) {
			return canFetchInvoices().then(function (allowed) {
				if (!allowed) {
					return null;
				}

				return LiveUpdateGuard.run("invoice", function () {
					return BillingAPI.getInvoice(patientId, invoiceId);
				}).then(
					function (resp) {
						const invoice = resp.data;
						return invoice && invoice.invoice_id ? invoice : null;
					},
					function () {
						// Deliberately silent: the poll will not re-announce this change, so the view stays as it
						// is until the next one. Nothing is logged - the response carries patient data.
						return null;
					}
				);
			});
		}

		/**
		 * Binds a scope's handlers to the invoice event types, so the listeners die with the view.
		 *
		 * @param scope    the view's scope
		 * @param onUpsert called for added/updated/paid
		 * @param onDelete called for deleted
		 */
		function listen(scope, onUpsert, onDelete) {
			UPSERT_EVENTS.forEach(function (eventName) {
				scope.$on(eventName, function (e, pushEvent) {
					onUpsert(toChange(pushEvent));
				});
			});
			scope.$on(PUSH_EVENTS.INVOICE_DELETED, function (e, pushEvent) {
				onDelete(toChange(pushEvent));
			});
		}

		/**
		 * Keeps a patient's invoice list current.
		 *
		 * @param scope the view's scope; listeners are removed when it is destroyed
		 * @param spec  patientId() - the patient whose invoices are listed; an event for anyone else is
		 *                  dropped without a request
		 *              invoices() - the invoice list, patched in place
		 *              onChanged() - re-sort or re-render after a patch
		 */
		function subscribeInvoiceList(scope, spec) {
			// Invoice ids with a fetch in flight, so two events for one invoice in a single poll batch
			// cannot race and leave the older response displayed.
			const fetching = {};

			/** The list, or null before the view's first load has landed. */
			function invoices() {
				const list = spec.invoices();
				return angular.isArray(list) ? list : null;
			}

			/**
			 * The patient id to fetch this invoice under, or null when the change is not this list's to
			 * apply.
			 *
			 * Not every invoice event names a patient - finalising one names the invoice alone - so a
			 * missing patient is not a reason to drop the event. When the list already holds the invoice
			 * it is this patient's by definition, and that is all the fetch needs.
			 */
			function resolvePatientId(change, list) {
				const viewPatientId = spec.patientId();
				if (!viewPatientId) {
					return null;
				}
				if (change.patientId) {
					return sameId(viewPatientId, change.patientId) ? viewPatientId : null;
				}
				return indexOfInvoice(list, change.invoiceId) !== -1 ? viewPatientId : null;
			}

			function onUpsert(change) {
				const list = invoices();
				if (!list || !change.invoiceId) {
					return;
				}

				const patientId = resolvePatientId(change, list);
				if (!patientId) {
					return;
				}

				if (fetching[change.invoiceId]) {
					return;
				}
				fetching[change.invoiceId] = true;

				fetchInvoice(patientId, change.invoiceId).then(function (invoice) {
					delete fetching[change.invoiceId];
					if (!invoice) {
						return;
					}

					// Re-read: the view may have re-fetched, or moved to another patient, in flight.
					const current = invoices();
					if (!current || !sameId(spec.patientId(), patientId)) {
						return;
					}

					const index = indexOfInvoice(current, change.invoiceId);
					if (index === -1) {
						current.push(invoice);
					} else {
						current[index] = invoice;
					}
					spec.onChanged();
				});
			}

			function onDelete(change) {
				// No patient check: an invoice id names one invoice, so holding it is reason enough to
				// drop it. That also covers a delete event that names no patient.
				const list = invoices();
				if (!list || !change.invoiceId) {
					return;
				}
				if (removeInvoice(list, change.invoiceId)) {
					spec.onChanged();
				}
			}

			listen(scope, onUpsert, onDelete);
		}

		/**
		 * Keeps the invoice summaries hanging off a calendar's appointments current, which is what the
		 * invoice icon and its hover total are drawn from.
		 *
		 * The summaries are re-read through the single-appointment endpoint, not the invoice endpoint.
		 * That endpoint attaches them the same way the appointment list does - same-day match in the
		 * practice's timezone, served from the back end's invoice summary cache, which every invoice
		 * change made through CatTrax writes through to before its event is published. The invoice
		 * endpoint goes to Xero directly: one invoice change used to send every open calendar showing that
		 * patient to Xero within the same poll, which tripped Xero's per-organisation concurrency limit,
		 * and a 429 there dropped the update silently - the icon stayed missing until the next login.
		 *
		 * A read that fails, or comes back flagged invoices_unavailable, keeps the icons on screen and is
		 * retried a few times on a backoff with jitter, so a burst of calendars do not retry in lockstep.
		 *
		 * @param scope the view's scope; listeners and pending retries die with it
		 * @param spec  appointments() - the raw appointment list, patched in place
		 *              onChanged() - re-render after a patch
		 */
		function subscribeAppointmentInvoices(scope, spec) {
			// Base delays before each retry; up to half as much again is added at random.
			const RETRY_DELAYS_MS = [5000, 15000, 45000];

			// Patient ids with a refresh in flight or waiting on a retry.
			const refreshing = {};

			// Patient ids that saw another event while refreshing. The read in flight may have been answered
			// before that change landed, so one more read follows it.
			const changedAgain = {};

			const retryTimers = [];
			let destroyed = false;

			scope.$on("$destroy", function () {
				destroyed = true;
				retryTimers.forEach(function (timer) {
					$timeout.cancel(timer);
				});
			});

			/** The list, or null before the view's first load has landed. */
			function appointments() {
				const list = spec.appointments();
				return angular.isArray(list) ? list : null;
			}

			/** The appointments on screen for one patient. */
			function appointmentsForPatient(list, patientId) {
				return list.filter(function (appointment) {
					return sameId(appointment.patient_id, patientId);
				});
			}

			/**
			 * The patient id to refresh for this invoice, or null when no icon on screen could change.
			 *
			 * An event that names a patient is only worth a request if that patient has an appointment
			 * on screen. An event that names none - finalising an invoice - is worth one if the invoice
			 * is already drawn on an appointment, which tells us whose it is.
			 */
			function resolvePatientId(change, list) {
				if (change.patientId) {
					return appointmentsForPatient(list, change.patientId).length > 0 ? change.patientId : null;
				}

				for (let i = 0; i < list.length; i++) {
					const summaries = list[i].invoice_list;
					if (angular.isArray(summaries) && indexOfInvoice(summaries, change.invoiceId) !== -1) {
						return list[i].patient_id;
					}
				}
				return null;
			}

			/**
			 * One appointment's invoice summaries as the back end now has them, or null when they could not
			 * be read - a failed request, or a response saying Xero could not be asked.
			 */
			function readInvoiceSummaries(appointment) {
				return LiveUpdateGuard.run("appointmentInvoices", function () {
					return AppointmentAPI.getAppointment(
						appointment.patient_id,
						appointment.patient_appointment_id,
						appointment.practice_id
					);
				}).then(
					function (resp) {
						const fresh = resp && resp.data;
						if (!fresh || fresh.invoices_unavailable === true) {
							return null;
						}
						return angular.isArray(fresh.invoice_list) ? fresh.invoice_list : [];
					},
					function () {
						// Deliberately silent: nothing is logged - the response carries patient data.
						return null;
					}
				);
			}

			/**
			 * Re-reads the invoice summaries of every appointment on screen for one patient and swaps in any
			 * that changed. Replacing the whole list per appointment also moves a re-dated invoice from one
			 * appointment to another, and drops one that no longer belongs.
			 *
			 * @param attempt how many retries have already been spent on this change
			 */
			function refreshPatient(patientId, attempt) {
				const list = appointments();
				const targets = list ? appointmentsForPatient(list, patientId) : [];
				if (destroyed || targets.length === 0) {
					delete refreshing[patientId];
					delete changedAgain[patientId];
					return;
				}

				refreshing[patientId] = true;
				delete changedAgain[patientId];

				$q.all(
					targets.map(function (appointment) {
						return readInvoiceSummaries(appointment).then(function (summaries) {
							return { appointmentId: appointment.patient_appointment_id, summaries: summaries };
						});
					})
				).then(function (results) {
					if (destroyed) {
						return;
					}

					// Re-read the list: the view may have re-fetched while this was in flight.
					const current = appointments() || [];
					let changed = false;
					let failed = false;

					results.forEach(function (result) {
						if (result.summaries === null) {
							failed = true;
							return;
						}
						current.forEach(function (appointment) {
							if (!sameId(appointment.patient_appointment_id, result.appointmentId)) {
								return;
							}
							const existing = angular.isArray(appointment.invoice_list) ? appointment.invoice_list : [];
							if (!angular.equals(existing, result.summaries)) {
								appointment.invoice_list = result.summaries;
								changed = true;
							}
						});
					});

					if (changed) {
						spec.onChanged();
					}

					if (changedAgain[patientId]) {
						refreshPatient(patientId, 0);
						return;
					}

					if (failed && attempt < RETRY_DELAYS_MS.length) {
						const base = RETRY_DELAYS_MS[attempt];
						const timer = $timeout(function () {
							retryTimers.splice(retryTimers.indexOf(timer), 1);
							refreshPatient(patientId, attempt + 1);
						}, base + Math.floor(Math.random() * base * 0.5));
						retryTimers.push(timer);
						return;
					}

					delete refreshing[patientId];
				});
			}

			function onUpsert(change) {
				const list = appointments();
				if (!list || !change.invoiceId) {
					return;
				}

				const patientId = resolvePatientId(change, list);
				if (!patientId) {
					return;
				}

				if (refreshing[patientId]) {
					changedAgain[patientId] = true;
					return;
				}
				refreshing[patientId] = true;

				canFetchInvoices().then(function (allowed) {
					if (!allowed) {
						delete refreshing[patientId];
						return;
					}
					refreshPatient(patientId, 0);
				});
			}

			function onDelete(change) {
				const list = appointments();
				if (!list || !change.invoiceId) {
					return;
				}

				// Every appointment on screen, not just this patient's: the id alone identifies the
				// invoice, and a stale icon is worth clearing wherever it is.
				let changed = false;
				list.forEach(function (appointment) {
					if (angular.isArray(appointment.invoice_list) && removeInvoice(appointment.invoice_list, change.invoiceId)) {
						changed = true;
					}
				});

				if (changed) {
					spec.onChanged();
				}
			}

			listen(scope, onUpsert, onDelete);
		}

		/** Whether an invoice has moved on from a working copy in a way that changes what can be paid on it. */
		function payableStateDiffers(copy, fresh) {
			return copy.status !== fresh.status || Number(copy.amount_due) !== Number(fresh.amount_due);
		}

		/**
		 * Tells a payment form when the invoice it is about to pay against has changed underneath it -
		 * settled by a terminal payment's webhook, by the funder, or by another user paying it from their
		 * own screen. The form's copy was taken when it opened and is deliberately never patched, so
		 * without this it stays valid and submits a payment the server refuses.
		 *
		 * The invoice is re-read before anything is reported, so an event for a change that leaves the
		 * form's figures standing - the echo of the form's own payment once it has re-read, say - costs a
		 * request but no interruption.
		 *
		 * @param scope the view's scope; listeners are removed when it is destroyed
		 * @param spec  patientId() - the patient the invoice belongs to
		 *              invoice() - the working copy being paid against, or null while no payment is open
		 *              onChanged(fresh) - the invoice as it now stands, or null if it was deleted
		 */
		function watchInvoice(scope, spec) {
			let fetching = false;

			/** The working copy, when it is the invoice the change names. */
			function watched(change) {
				const copy = spec.invoice();
				return copy && change.invoiceId && sameId(copy.invoice_id, change.invoiceId) ? copy : null;
			}

			function onUpsert(change) {
				const patientId = spec.patientId();
				if (!patientId || !watched(change) || fetching) {
					return;
				}
				fetching = true;

				fetchInvoice(patientId, change.invoiceId).then(function (fresh) {
					fetching = false;
					// Re-read: the form may have closed, or moved to another invoice, in flight.
					const copy = watched(change);
					if (fresh && copy && payableStateDiffers(copy, fresh)) {
						spec.onChanged(fresh);
					}
				});
			}

			function onDelete(change) {
				if (watched(change)) {
					spec.onChanged(null);
				}
			}

			listen(scope, onUpsert, onDelete);
		}

		const service = {
			subscribeInvoiceList: subscribeInvoiceList,
			subscribeAppointmentInvoices: subscribeAppointmentInvoices,
			watchInvoice: watchInvoice,
		};

		return service;
	},
]);
